import React from 'react';
import OptimizedImage from '@/components/OptimizedImage';
import { useHeroImageSettings } from '@/hooks/useHeroImageSettings';
import { useIsMobile } from '@/hooks/use-mobile';

interface HeroImageProps {
  pageKey: string;
  className?: string;
  priority?: boolean;
  children?: React.ReactNode;
  fallbackImageFile?: string;
}

export const HeroImage: React.FC<HeroImageProps> = ({
  pageKey,
  className = '',
  priority = true,
  children,
  fallbackImageFile
}) => {
  const { settings, isLoading: settingsLoading, getImageUrl } = useHeroImageSettings(pageKey);
  const isMobile = useIsMobile();
  
  // Always use the current image URL, no complex fallback logic
  const imageUrl = getImageUrl(fallbackImageFile);

  // Use separate position settings for mobile and desktop
  const positionX = isMobile ? settings.mobile_position_x : settings.desktop_position_x;
  const positionY = isMobile ? settings.mobile_position_y : settings.desktop_position_y;

  // Clear cache on mount to ensure fresh images
  React.useEffect(() => {
    import('@/services/imageCache').then(({ imageCache }) => {
      // Clear any stale cache on component mount
      if (imageUrl) {
        imageCache.clearImage(imageUrl);
      }
    });
  }, []);

  const containerStyle = {
    '--mobile-height': settings.mobile_height,
    '--desktop-height': settings.desktop_height,
    '--object-position': `${positionX}% ${positionY}%`,
    '--object-fit': settings.object_fit
  } as React.CSSProperties;

  // If settings are loading, don't render anything (page will show skeleton)
  if (settingsLoading) {
    return null;
  }

  // Show placeholder if no image URL
  if (!imageUrl) {
    return (
      <div 
        className={`relative w-full overflow-hidden h-[var(--mobile-height)] md:h-[var(--desktop-height)] ${className}`}
        style={containerStyle}
      >
        <div className="absolute inset-0 bg-secondary/10" />
        <div className="absolute inset-0 z-10 flex items-center justify-center">
          {children}
        </div>
      </div>
    );
  }

  // Render OptimizedImage directly - it will handle its own fade-in
  return (
    <div 
      className={`relative w-full overflow-hidden h-[var(--mobile-height)] md:h-[var(--desktop-height)] ${className}`}
      style={containerStyle}
    >
      <div 
        className="absolute inset-0"
        style={{
          '--object-fit': settings.object_fit,
          '--object-position': `${positionX}% ${positionY}%`
        } as React.CSSProperties}
      >
        <OptimizedImage
          src={imageUrl}
          alt={`Hero image for ${pageKey}`}
          className="w-full h-full [object-fit:var(--object-fit)] [object-position:var(--object-position)]"
          priority={priority}
          responsive={false}
          layout="hero"
          key={imageUrl}
        />
      </div>
      <div className="absolute inset-0 z-10 flex items-center justify-center">
        {children}
      </div>
    </div>
  );
};